import React, { useState, useEffect } from 'react';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import axios from 'axios';
import moment from 'moment';
import 'moment-timezone';
import Loader from '../Loader/Loader';
import { getUserTimezone } from '../../utils/timezoneUtils';
import './AGPChart.css';

// AGP Chart Component - Displays 15-day glucose pattern with timezone-aware date handling
// Helper: Convert UTC timestamp to local moment
const toLocalTime = (timestamp) => moment.utc(timestamp).local();

// Helper: Get UTC date string for start of a local day
// This accounts for timezone offset when querying the backend
const getUTCDateForLocalDayStart = (localDate) => {
  return moment(localDate).startOf('day').utc().format('YYYY-MM-DD');
};

// Helper: Get UTC date string for end of a local day
const getUTCDateForLocalDayEnd = (localDate) => {
  return moment(localDate).endOf('day').utc().format('YYYY-MM-DD');
};

const base_url = process.env.REACT_APP_BASE_URL;

// Filter out bad CGM readings - only accept realistic glucose values (36-450 mg/dL)
const isValidGlucose = (value) => {
  if (value === null || value === undefined || isNaN(value)) return false;
  return value >= 36 && value <= 450;
};

// Calculate percentile from sorted array
const percentile = (arr, p) => {
  if (arr.length === 0) return null;
  const sorted = [...arr].sort((a, b) => a - b);
  const index = (p / 100) * (sorted.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  if (lower === upper) return sorted[lower];
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (index - lower);
};

function AGPChart() {
  // Get UID from URL params (reactive) instead of localStorage (not reactive)
  const queryParams = new URLSearchParams(window.location.search);
  const UID = queryParams.get("user");
  const DATE = localStorage.getItem("DATE");
  const user = localStorage.getItem("user")?.replace(/['"]+/g, '');
  const [userEmail, setUserEmail] = useState(null);
  const [lastFetchedUID, setLastFetchedUID] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [agpData, setAgpData] = useState([]);
  const [dataAvailable, setDataAvailable] = useState(false);
  const [agpDateRange, setAgpDateRange] = useState(null);
  const [deviceBootAt, setDeviceBootAtState] = useState(localStorage.getItem("deviceBootAt") || null);

  // Watch for deviceBootAt changes in localStorage
  useEffect(() => {
    const checkDeviceDates = () => {
      const stored = localStorage.getItem("deviceBootAt");
      if (stored && stored !== deviceBootAt) {
        setDeviceBootAtState(stored);
      }
    };
    const interval = setInterval(checkDeviceDates, 500);
    return () => clearInterval(interval);
  }, [deviceBootAt]);

  // Watch for email changes in localStorage
  // Only accept email if it matches the current UID's profile
  useEffect(() => {
    const checkEmail = () => {
      const currentEmail = localStorage.getItem("email");
      const storedUID = localStorage.getItem("UID")?.replace(/['"]+/g, '');
      
      // Only update email if localStorage UID matches our current UID
      // This prevents race condition where old email is still in localStorage
      if (currentEmail && storedUID === UID && currentEmail !== userEmail) {
        setUserEmail(currentEmail);
      }
    };
    checkEmail();
    const interval = setInterval(checkEmail, 300);
    return () => clearInterval(interval);
  }, [userEmail, UID]);

  // Reset data when UID changes (prevents showing old user's data)
  useEffect(() => {
    if (UID && lastFetchedUID && UID !== lastFetchedUID) {
      setAgpData([]);
      setUserEmail(null); // Also reset email to force re-fetch
      setDataAvailable(false);
      setLoading(true);
    }
  }, [UID, lastFetchedUID]);

  // Fetch and process AGP data
  const fetchAGPData = async () => {
    if (!UID || !userEmail) return;
    
    setLoading(true);
    setLastFetchedUID(UID); // Track which UID we're fetching for
    
    // Use device boot/expire dates if available, otherwise fall back to 15-day window from DATE
    const deviceExpireAt = localStorage.getItem("deviceExpireAt");
    
    let startDate, endDate;
    if (deviceBootAt && deviceExpireAt) {
      startDate = moment.unix(parseInt(deviceBootAt)).format('YYYY-MM-DD');
      // Use today or expire date, whichever is earlier
      const expireDate = moment.unix(parseInt(deviceExpireAt));
      const today = moment();
      endDate = (expireDate.isBefore(today) ? expireDate : today).format('YYYY-MM-DD');
    } else {
      const localEndDate = moment(DATE);
      const localStartDate = moment(DATE).subtract(14, 'days');
      startDate = localStartDate.format('YYYY-MM-DD');
      endDate = localEndDate.format('YYYY-MM-DD');
    }
    
    setAgpDateRange({ start: startDate, end: endDate });
    
    // Get user's timezone
    const userTimezone = getUserTimezone();
    
    try {
      // Backend now accepts timezone parameter and returns timestamps in user's local time
      const response = await axios.post(
        base_url + '/admin/influxdb/glucose-data?aggregate=15min',
        {
          uuid: UID,
          email: userEmail,
          start_date: startDate,  // Local date
          end_date: endDate,      // Local date
          timezone: userTimezone, // NEW: User's timezone
          is_admin: 'True'
        },
        { headers: { "Authorization": `Bearer ${user}` } }
      );
      
      if (response.data.response !== "Error while fetching data") {
        const rawData = response.data.response?.cgm_data || [];
        
        if (rawData.length === 0) {
          setDataAvailable(false);
          setAgpData([]);
          setLoading(false);
          return;
        }
        
        // Group data by time of day (1-hour buckets for faster processing)
        const timeBuckets = {};
        
        // Initialize 24 buckets (1-hour intervals over 24 hours)
        for (let i = 0; i < 24; i++) {
          const timeKey = `${i.toString().padStart(2, '0')}:00`;
          timeBuckets[timeKey] = [];
        }
        
        // Process each reading into time buckets
        // Filter readings to device boot/expire range or fallback
        const localStartBoundary = deviceBootAt
          ? moment.unix(parseInt(deviceBootAt)).startOf('day')
          : moment(DATE).subtract(14, 'days').startOf('day');
        const deviceExpireAtBoundary = localStorage.getItem("deviceExpireAt");
        const localEndBoundary = deviceExpireAtBoundary
          ? moment.unix(parseInt(deviceExpireAtBoundary)).endOf('day')
          : moment(DATE).endOf('day');
        
        rawData.forEach(reading => {
          const glucoseValue = reading.glucose_avg || reading.glucose || reading.value;
          const timestamp = reading.timestamp || reading.time || reading._time;
          
          if (glucoseValue && timestamp) {
            if (isValidGlucose(glucoseValue)) {
              // Backend returns local timestamps - parse directly
              const readingTime = moment(timestamp);
              
              // Filter to only include readings within the local date range
              if (readingTime.isBefore(localStartBoundary) || readingTime.isAfter(localEndBoundary)) {
                return;
              }
              
              const hour = readingTime.hour();
              const timeKey = `${hour.toString().padStart(2, '0')}:00`;
              
              if (timeBuckets[timeKey]) {
                timeBuckets[timeKey].push(glucoseValue);
              }
            }
          }
        });
        
        // Calculate percentiles for each time bucket
        const processedData = Object.entries(timeBuckets).map(([time, values]) => {
          if (values.length === 0) {
            return {
              time,
              p5: null,
              p25: null,
              p50: null,
              p75: null,
              p95: null,
              // Band ranges for proper area chart display
              band95: [null, null],
              band75: [null, null]
            };
          }
          
          const p5Val = percentile(values, 5);
          const p25Val = percentile(values, 25);
          const p50Val = percentile(values, 50);
          const p75Val = percentile(values, 75);
          const p95Val = percentile(values, 95);
          
          return {
            time,
            p5: p5Val,
            p25: p25Val,
            p50: p50Val,
            p75: p75Val,
            p95: p95Val,
            // Band ranges for proper area chart display [min, max]
            band95: [p5Val, p95Val],
            band75: [p25Val, p75Val]
          };
        });
        
        setAgpData(processedData);
        setDataAvailable(processedData.some(d => d.p50 !== null));
      } else {
        setDataAvailable(false);
        setAgpData([]);
      }
    } catch (error) {
      console.error('Error fetching AGP data:', error);
      setDataAvailable(false);
      setAgpData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userEmail && UID) {
      fetchAGPData();
    }
  }, [userEmail, UID, DATE, deviceBootAt]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      if (!data || data.p50 === null) return null;
      
      return (
        <div className="agp-tooltip">
          <p className="agp-tooltip-time">{label}</p>
          <p className="agp-tooltip-median">Median: {data.p50?.toFixed(0)} mg/dL</p>
          <p className="agp-tooltip-range">25-75%: {data.p25?.toFixed(0)} - {data.p75?.toFixed(0)}</p>
          <p className="agp-tooltip-range">5-95%: {data.p5?.toFixed(0)} - {data.p95?.toFixed(0)}</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className='col-wrapper exercise full-width-blk agp-chart-wrapper'>
        <div className='filter-wrapper'>
          <div className='left-div'>
            <div className='total-cal dark-text'>AGP Chart</div>
          </div>
        </div>
        <Loader />
      </div>
    );
  }

  return (
    <div className='col-wrapper exercise full-width-blk agp-chart-wrapper'>
      <div className='filter-wrapper'>
        <div className='left-div'>
          <div className='total-cal dark-text'>AGP Chart</div>
          <div className='agp-subtitle'>
            {agpDateRange
              ? `${moment(agpDateRange.start).format('MMM D')} - ${moment(agpDateRange.end).format('MMM D, YYYY')}`
              : '15-day glucose pattern (mg/dL)'}
          </div>
        </div>
      </div>
      
      {/* Legend */}
      <div className="agp-legend">
        <div className="agp-legend-item">
          <span className="agp-legend-line median"></span>
          <span>50% Range</span>
        </div>
        <div className="agp-legend-item">
          <span className="agp-legend-box iqr"></span>
          <span>25-75% Range</span>
        </div>
        <div className="agp-legend-item">
          <span className="agp-legend-box outer"></span>
          <span>5-95% Range</span>
        </div>
      </div>

      {!dataAvailable ? (
        <div className="agp-no-data">No glucose data available for AGP chart</div>
      ) : (
        <ResponsiveContainer width="100%" height={350}>
          <ComposedChart data={agpData} margin={{ top: 20, right: 30, left: 10, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis 
              dataKey="time" 
              axisLine={{ stroke: '#ccc' }}
              tickLine={false}
              tick={{ fontSize: 11, fill: '#666' }}
              interval={3}
              tickFormatter={(value) => value}
            />
            <YAxis 
              domain={[0, 270]}
              ticks={[0, 54, 108, 162, 216, 270]}
              axisLine={{ stroke: '#ccc' }}
              tickLine={false}
              tick={{ fontSize: 11, fill: '#666' }}
              label={{ value: 'mg/dL', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 12, fill: '#666' } }}
            />
            <Tooltip content={<CustomTooltip />} />
            
            {/* Target range reference lines (70-180 mg/dL) */}
            <ReferenceLine y={70} stroke="#4CAF50" strokeDasharray="5 5" strokeWidth={1} />
            <ReferenceLine y={180} stroke="#E53935" strokeDasharray="5 5" strokeWidth={1} />
            
            {/* 5-95% Range (outer band - lighter) */}
            <Area
              type="monotone"
              dataKey="band95"
              stroke="none"
              fill="#B8C5E8"
              fillOpacity={0.5}
              connectNulls
            />
            
            {/* 25-75% Range (inner band - darker) */}
            <Area
              type="monotone"
              dataKey="band75"
              stroke="none"
              fill="#5C6BC0"
              fillOpacity={0.6}
              connectNulls
            />
            
            {/* 50% Median line */}
            <Line
              type="monotone"
              dataKey="p50"
              stroke="#1a237e"
              strokeWidth={2.5}
              dot={false}
              connectNulls
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
      
      <div className="agp-footer">
        <span>Time of Day (00:00 - 24:00)</span>
      </div>
    </div>
  );
}

export default AGPChart;

